import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Users, Plus, Search, RefreshCw, Trash2, Save, ArrowLeft, Loader2,
  Download, Building2, MapPin, Phone, Mail, FileText, Tag, Power,
  Briefcase, BadgeCheck, Receipt, Wand2, Image as ImageIcon, X,
  Send, Eye, CalendarClock, Merge,
} from "lucide-react";
import { PageHeader } from "../components/Layout";
import { api, formatApiError, API_BASE } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { downloadCsv } from "../lib/exporters";

const ROLE_OPTIONS = [
  { v: "carrier", l: "Carrier", icon: Briefcase, color: "#006E9E" },
  { v: "customer", l: "Customer", icon: Users, color: "#99D420" },
  { v: "vendor", l: "Vendor", icon: BadgeCheck, color: "#F1471D" },
];

const EMPTY = {
  code: "", legal_name: "", trading_name: "", roles: ["customer"],
  address: { street: "", postcode: "", city: "", country: "" },
  vat_number: "", tax_number: "", chamber_of_commerce_no: "", tin: "",
  primary_contact: { name: "", function: "", phone: "", email: "" },
  billing_email: "",
  billing_cc: "",
  waybill_email: "",
  ar_contact: { name: "", function: "", phone: "", email: "" },
  edi: {
    partner_profile: "",
    sender_id: "", receiver_id: "",
    email: "", cc_emails: [], auto_send: true,
    depot_code: "", depot_location: "", unh_prefix: "",
    iso_type_map: null, batch_mode: "IMMEDIATE",
  },
  daily_stock_report: {
    enabled: false,
    recipients: [],
    cc_emails: [],
    template: "CONTAINER",
    format: "pdf",
    last_sent_at: null,
    last_status: null,
  },
  default_currency: "USD",
  payment_terms_days: 30,
  notes: "", tags: [], aliases: [], active: true,
};

export default function Partners() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "supervisor";
  const canDelete = user?.role === "admin";
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coverage, setCoverage] = useState({ depots: [], coverage: {} });
  const [filter, setFilter] = useState({ role: "", q: "", active: "" });
  const [editing, setEditing] = useState(null); // null | "new" | partner object | { _prefill: {…} }
  const [importing, setImporting] = useState(false);

  // Deep-link support: /partners?new=1&legal_name=BUNGE&code=BUNGE&role=customer
  // Used by the "Add as Partner" buttons on Gate Events / Wash Station.
  useEffect(() => {
    if (!canEdit) return;
    if (searchParams.get("new") !== "1") return;
    const legal = searchParams.get("legal_name") || "";
    const code = (searchParams.get("code") || legal).toUpperCase().replace(/\s+/g, "_").slice(0, 20);
    const role = searchParams.get("role") || "customer";
    setEditing({
      ...EMPTY,
      legal_name: legal,
      code,
      roles: [role],
      tags: ["from-operational-data"],
    });
    // Drop the query string so navigating back doesn't re-trigger this
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, canEdit]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.role) params.append("role", filter.role);
      if (filter.q) params.append("q", filter.q);
      if (filter.active !== "") params.append("active", filter.active);
      const { data } = await api.get(`/partners?${params.toString()}`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  // Tariff coverage matrix — loaded once on mount, refreshed after any
  // save/import so the chips stay honest. Not folded into `load` to avoid
  // re-fetching on every filter keystroke (coverage doesn't depend on
  // the role / search filter).
  const loadCoverage = useCallback(async () => {
    try {
      const { data } = await api.get("/partners/tariff-coverage");
      setCoverage(data || { depots: [], coverage: {} });
    } catch { setCoverage({ depots: [], coverage: {} }); }
  }, []);
  useEffect(() => { loadCoverage(); }, [loadCoverage]);

  const runImport = async (includeCustomers = false) => {
    const msg = includeCustomers
      ? "Auto-import carriers AND customer suggestions from gate events / wash orders?\n\nThis will create one partner row per distinct customer name found in operational data. Existing partners are NOT overwritten. Re-running is safe (idempotent)."
      : "Auto-import carriers only (from tariffs, EDI recipients, and gate events)?\n\nNo customers will be created. Existing partners are NOT overwritten.";
    if (!window.confirm(msg)) return;
    setImporting(true);
    try {
      const params = new URLSearchParams({
        include_carriers: "true",
        include_customers: String(!!includeCustomers),
      });
      const { data } = await api.post(`/partners/import-from-existing?${params.toString()}`);
      toast.success(`+${data.created_carriers} carriers · +${data.created_customers} customers (total ${data.total_partners})`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImporting(false); }
  };

  const stats = useMemo(() => {
    const carriers = items.filter((p) => (p.roles || []).includes("carrier")).length;
    const customers = items.filter((p) => (p.roles || []).includes("customer")).length;
    const vendors = items.filter((p) => (p.roles || []).includes("vendor")).length;
    const inactive = items.filter((p) => !p.active).length;
    return { total: items.length, carriers, customers, vendors, inactive };
  }, [items]);

  const exportCsv = () => {
    if (!items.length) return;
    downloadCsv(items, `partners_${new Date().toISOString().slice(0, 10)}`, [
      { key: "code", label: "Code" },
      { key: "legal_name", label: "Legal Name" },
      { key: "trading_name", label: "Trading Name" },
      { key: "roles", label: "Roles", format: (v) => (v || []).join(" | ") },
      { key: "vat_number", label: "VAT" },
      { key: "tin", label: "TIN" },
      { key: "billing_email", label: "Billing Email" },
      { key: "default_currency", label: "Currency" },
      { key: "address.city", label: "City", format: (_, p) => p?.address?.city || "" },
      { key: "address.country", label: "Country", format: (_, p) => p?.address?.country || "" },
      { key: "active", label: "Active", format: (v) => (v ? "Yes" : "No") },
    ]);
  };

  if (editing) {
    const partnerToEdit =
      editing === "new" ? { ...EMPTY } :
      editing && editing.id ? editing :
      editing; // pre-filled object from deep-link
    return (
      <PartnerEditor
        partner={partnerToEdit}
        canDelete={canDelete}
        onCancel={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    );
  }

  return (
    <div data-testid="partners-page">
      <PageHeader
        title="Customers & Partners"
        subtitle="Master record of every carrier, customer, and vendor (NAW, tax IDs, EDI, billing email)."
        testid="partners-header"
        actions={
          <>
            <button className="ps-btn ps-btn-secondary" onClick={load} data-testid="partners-refresh">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button className="ps-btn ps-btn-secondary" onClick={exportCsv} disabled={!items.length} data-testid="partners-csv">
              <Download className="w-4 h-4" /> CSV
            </button>
            {canEdit && (
              <button className="ps-btn ps-btn-secondary" onClick={() => runImport(false)} disabled={importing} data-testid="partners-import" title="Auto-import carriers from tariffs / EDI recipients / gate events">
                {importing ? <><Loader2 className="w-4 h-4 animate-spin" /> Importing…</> : <><Wand2 className="w-4 h-4" /> Auto-import carriers</>}
              </button>
            )}
            {canEdit && (
              <button className="ps-btn ps-btn-secondary" onClick={() => runImport(true)} disabled={importing} data-testid="partners-import-customers" title="Also auto-import every distinct customer name from gate events / wash orders (can create many rows!)">
                <Users className="w-4 h-4" /> + Customers
              </button>
            )}
            {canEdit && (
              <button className="ps-btn ps-btn-secondary" onClick={() => navigate("/partners/duplicates")} data-testid="partners-duplicates" title="Find and merge duplicate partner records">
                <Merge className="w-4 h-4" /> Duplicates
              </button>
            )}
            {canEdit && (
              <button className="ps-btn ps-btn-primary" onClick={() => setEditing("new")} data-testid="partners-new">
                <Plus className="w-4 h-4" /> New partner
              </button>
            )}
          </>
        }
      />

      <div className="p-6 sm:p-8 space-y-4">
        {/* KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="partners-kpis">
          <KpiCard label="Total" value={stats.total} accent="#003147" />
          <KpiCard label="Carriers" value={stats.carriers} accent="#006E9E" />
          <KpiCard label="Customers" value={stats.customers} accent="#99D420" />
          <KpiCard label="Vendors" value={stats.vendors} accent="#F1471D" />
          <KpiCard label="Inactive" value={stats.inactive} accent="#5A4E42" muted />
        </div>

        {/* Filters */}
        <div className="border border-gray-200 bg-white p-4 grid sm:grid-cols-4 gap-3" data-testid="partners-filters">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={filter.q}
              onChange={(e) => setFilter({ ...filter, q: e.target.value })}
              placeholder="Search code / name…"
              className="ps-input pl-9"
              data-testid="partners-search"
            />
          </div>
          <select
            value={filter.role}
            onChange={(e) => setFilter({ ...filter, role: e.target.value })}
            className="ps-input font-mono"
            data-testid="partners-role-filter"
          >
            <option value="">All roles</option>
            {ROLE_OPTIONS.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
          </select>
          <select
            value={filter.active}
            onChange={(e) => setFilter({ ...filter, active: e.target.value })}
            className="ps-input font-mono"
            data-testid="partners-active-filter"
          >
            <option value="">Active + Inactive</option>
            <option value="true">Active only</option>
            <option value="false">Inactive only</option>
          </select>
          <div className="text-xs text-gray-500 self-center font-mono">
            {loading ? "Loading…" : `${items.length} partner${items.length === 1 ? "" : "s"}`}
          </div>
        </div>

        {/* Table */}
        <div className="border border-gray-200 bg-white overflow-x-auto">
          <table className="ps-table" data-testid="partners-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Legal Name</th>
                <th>Roles</th>
                <th>Tariff coverage</th>
                <th>City / Country</th>
                <th>Billing email</th>
                <th>Currency</th>
                <th>Active</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="text-center py-10 text-gray-400">Loading…</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={9} className="text-center py-10 text-gray-400">
                  No partners. Click <strong>Auto-import</strong> to seed from existing operational data.
                </td></tr>
              )}
              {!loading && items.map((p) => (
                <tr key={p.id} data-testid={`partner-row-${p.code}`}>
                  <td className="font-mono font-bold">{p.code}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      {p.logo_url && <img src={p.logo_url} alt="" className="w-5 h-5 object-contain" />}
                      <span>{p.legal_name}</span>
                    </div>
                    {p.trading_name && p.trading_name !== p.legal_name && (
                      <div className="text-[10px] text-gray-500 italic mt-0.5">{p.trading_name}</div>
                    )}
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {(p.roles || []).map((r) => {
                        const opt = ROLE_OPTIONS.find((x) => x.v === r);
                        if (!opt) return null;
                        return (
                          <span
                            key={r}
                            className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                            style={{ background: `${opt.color}1F`, color: opt.color }}
                          >
                            {opt.l}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td>
                    <TariffChips partner={p} coverage={coverage} />
                  </td>
                  <td className="text-xs">
                    {p.address?.city ? `${p.address.city}${p.address.country ? `, ${p.address.country}` : ""}` : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="text-xs font-mono">{p.billing_email || <span className="text-gray-400">—</span>}</td>
                  <td className="font-mono text-xs">{p.default_currency}</td>
                  <td>
                    {p.active ? (
                      <span className="ps-pill ps-pill-success"><span className="dot" /> Active</span>
                    ) : (
                      <span className="ps-pill ps-pill-neutral"><span className="dot" /> Inactive</span>
                    )}
                  </td>
                  <td className="text-right">
                    <button
                      className="ps-btn ps-btn-secondary h-7 px-3 text-xs"
                      onClick={() => setEditing(p)}
                      data-testid={`open-partner-${p.code}`}
                    >
                      View / Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, accent, muted }) {
  return (
    <div className={`border bg-white px-4 py-3 ${muted ? "border-gray-200 opacity-60" : "border-gray-200"}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{label}</div>
      <div className="font-mono font-black text-2xl mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function PartnerEditor({ partner, canDelete, onCancel, onSaved }) {
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    ...partner,
    address: { ...EMPTY.address, ...(partner.address || {}) },
    primary_contact: { ...EMPTY.primary_contact, ...(partner.primary_contact || {}) },
    ar_contact: { ...EMPTY.ar_contact, ...(partner.ar_contact || {}) },
    edi: { ...EMPTY.edi, ...(partner.edi || {}) },
    daily_stock_report: { ...EMPTY.daily_stock_report, ...(partner.daily_stock_report || {}) },
    tags: partner.tags || [],
    aliases: partner.aliases || [],
    roles: partner.roles?.length ? partner.roles : ["customer"],
  }));
  const [tagInput, setTagInput] = useState("");
  const [aliasInput, setAliasInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const isNew = !partner.id;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNested = (parent, k, v) => setForm((f) => ({ ...f, [parent]: { ...f[parent], [k]: v } }));
  const toggleRole = (r) => {
    const has = form.roles.includes(r);
    set("roles", has ? form.roles.filter((x) => x !== r) : [...form.roles, r]);
  };

  const save = async () => {
    if (!form.code.trim()) { toast.error("Code is required"); return; }
    if (!form.legal_name.trim()) { toast.error("Legal name is required"); return; }
    if (!form.roles.length) { toast.error("At least one role is required"); return; }
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post("/partners", form);
        toast.success(`${data.code} created`);
      } else {
        const { id, created_at, updated_at, created_by, updated_by, ...patch } = form;
        await api.patch(`/partners/${partner.id}`, patch);
        toast.success(`${form.code} saved`);
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete partner ${partner.code}? This cannot be undone.`)) return;
    try {
      await api.delete(`/partners/${partner.id}`);
      toast.success(`Deleted ${partner.code}`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/partners/${partner.id}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("logo_url", data.logo_url);
      toast.success("Logo uploaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploadingLogo(false); }
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (!form.tags.includes(t)) set("tags", [...form.tags, t]);
    setTagInput("");
  };

  return (
    <div data-testid="partner-editor">
      <PageHeader
        title={isNew ? "New partner" : form.legal_name || form.code}
        subtitle={isNew ? "Create a new carrier, customer, or vendor record." : `Edit master data for ${form.code}.`}
        testid="partner-editor-header"
        actions={
          <>
            <button className="ps-btn ps-btn-secondary" onClick={onCancel}><ArrowLeft className="w-4 h-4" /> Back</button>
            {!isNew && canDelete && (
              <button className="ps-btn ps-btn-secondary text-red-600" onClick={remove} data-testid="partner-delete">
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            )}
            <button className="ps-btn ps-btn-primary" onClick={save} disabled={saving} data-testid="partner-save">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save</>}
            </button>
          </>
        }
      />

      <div className="p-6 sm:p-8 space-y-4 max-w-5xl">
        {/* IDENTITY */}
        <Section icon={Building2} title="Identity">
          <Field label="Code *" value={form.code} onChange={(v) => set("code", v.toUpperCase())} mono testid="field-code" />
          <Field label="Legal name *" value={form.legal_name} onChange={(v) => set("legal_name", v)} testid="field-legal-name" />
          <Field label="Trading name" value={form.trading_name || ""} onChange={(v) => set("trading_name", v)} testid="field-trading-name" />
          <div className="sm:col-span-3">
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Roles *</label>
            <div className="flex gap-2 flex-wrap">
              {ROLE_OPTIONS.map((r) => {
                const Icon = r.icon;
                const on = form.roles.includes(r.v);
                return (
                  <button
                    key={r.v}
                    onClick={() => toggleRole(r.v)}
                    data-testid={`role-${r.v}`}
                    className={`px-3 py-2 border rounded text-xs font-bold inline-flex items-center gap-1.5 transition-colors ${
                      on
                        ? "text-white"
                        : "border-gray-200 text-gray-500 bg-white hover:border-gray-400"
                    }`}
                    style={on ? { background: r.color, borderColor: r.color } : {}}
                  >
                    <Icon className="w-3.5 h-3.5" /> {r.l}
                  </button>
                );
              })}
            </div>
          </div>
          <Field label="Default currency" value={form.default_currency} onChange={(v) => set("default_currency", v.toUpperCase())} mono testid="field-default-currency" />
          {!isNew && form.code && form.default_currency && (
            <button
              type="button"
              onClick={async () => {
                const target = (form.default_currency || "").toUpperCase();
                const code = form.code;
                try {
                  const { data } = await api.post(`/admin/migrations/align-partner-currency/${code}`, { default_currency: target, apply: false });
                  const m = data.matches || {};
                  const total = (m.partners || 0) + (m.gate_tariff || 0) + (m.wash_tariff_rate_rows || 0) + (m.cargo_products || 0);
                  if (total === 0) {
                    toast.success(`${code} is already fully aligned to ${target}.`);
                    return;
                  }
                  if (!window.confirm(
                    `Align ${code} to ${target}?\n\n` +
                    `• Partner default:        ${m.partners || 0}\n` +
                    `• Gate tariff:            ${m.gate_tariff || 0}\n` +
                    `• Wash tariff rate rows:  ${m.wash_tariff_rate_rows || 0}\n` +
                    `• Cargo product rates:    ${m.cargo_products || 0}\n\n` +
                    `Numeric rates stay the same — only the currency label flips.`
                  )) return;
                  await api.post(`/admin/migrations/align-partner-currency/${code}`, { default_currency: target, apply: true });
                  toast.success(`Aligned ${code} to ${target}.`);
                  const { data: p2 } = await api.get(`/partners/${code}`);
                  setForm({ ...form, default_currency: p2.default_currency || target });
                } catch (e) {
                  toast.error(formatApiError(e));
                }
              }}
              className="ps-btn ps-btn-secondary text-xs h-9 self-end"
              data-testid="align-partner-currency-btn"
              title="Flip this partner's gate tariff, wash rates, and cargo product rates to the default currency"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Align all rates to {(form.default_currency || "").toUpperCase()}
            </button>
          )}
          <Field label="Payment terms (days)" type="number" value={form.payment_terms_days} onChange={(v) => set("payment_terms_days", Number(v))} mono />
          <div className="self-end">
            <label className="ps-input inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} data-testid="field-active" />
              <Power className="w-3.5 h-3.5" /> {form.active ? "Active" : "Inactive"}
            </label>
          </div>
        </Section>

        {/* ADDRESS */}
        <Section icon={MapPin} title="Address (NAW)">
          <Field label="Street + No." value={form.address.street || ""} onChange={(v) => setNested("address", "street", v)} testid="field-street" cls="sm:col-span-2" />
          <Field label="Postcode" value={form.address.postcode || ""} onChange={(v) => setNested("address", "postcode", v)} mono testid="field-postcode" />
          <Field label="City" value={form.address.city || ""} onChange={(v) => setNested("address", "city", v)} testid="field-city" />
          <Field label="Country" value={form.address.country || ""} onChange={(v) => setNested("address", "country", v)} testid="field-country" />
        </Section>

        {/* TAX IDs */}
        <Section icon={Receipt} title="Tax & Registration">
          <Field label="VAT number" value={form.vat_number || ""} onChange={(v) => set("vat_number", v)} mono testid="field-vat" />
          <Field label="Tax number" value={form.tax_number || ""} onChange={(v) => set("tax_number", v)} mono />
          <Field label="Chamber of Commerce no." value={form.chamber_of_commerce_no || ""} onChange={(v) => set("chamber_of_commerce_no", v)} mono />
          <Field label="Ghana TIN" value={form.tin || ""} onChange={(v) => set("tin", v)} mono testid="field-tin" />
        </Section>

        {/* CONTACTS */}
        <Section icon={Phone} title="Primary contact">
          <Field label="Name" value={form.primary_contact.name || ""} onChange={(v) => setNested("primary_contact", "name", v)} testid="field-pc-name" />
          <Field label="Function / title" value={form.primary_contact.function || ""} onChange={(v) => setNested("primary_contact", "function", v)} />
          <Field label="Phone" value={form.primary_contact.phone || ""} onChange={(v) => setNested("primary_contact", "phone", v)} mono />
          <Field label="Email" value={form.primary_contact.email || ""} onChange={(v) => setNested("primary_contact", "email", v)} mono cls="sm:col-span-2" />
        </Section>

        <Section icon={Mail} title="Billing & AR">
          <Field label="Billing email *" value={form.billing_email || ""} onChange={(v) => set("billing_email", v)} mono testid="field-billing-email" cls="sm:col-span-3" />
          <Field label="Billing CC (comma-separated)" value={form.billing_cc || ""} onChange={(v) => set("billing_cc", v)} mono testid="field-billing-cc" cls="sm:col-span-3" />
          <Field label="AR contact name" value={form.ar_contact.name || ""} onChange={(v) => setNested("ar_contact", "name", v)} />
          <Field label="AR contact phone" value={form.ar_contact.phone || ""} onChange={(v) => setNested("ar_contact", "phone", v)} mono />
          <Field label="AR contact email" value={form.ar_contact.email || ""} onChange={(v) => setNested("ar_contact", "email", v)} mono />
          {/* Auto-waybill opt-in — leave blank to disable. Comma-separated for
              multiple recipients. Only meaningful for carrier partners since
              waybills fire off gate events where this partner is the carrier. */}
          {form.roles.includes("carrier") && (
            <>
              <Field
                label="Waybill auto-email (optional)"
                value={form.waybill_email || ""}
                onChange={(v) => set("waybill_email", v)}
                mono
                testid="field-waybill-email"
                cls="sm:col-span-3"
                placeholder="ops@carrier.com, fleet@carrier.com"
              />
              <div className="sm:col-span-3 text-[11px] text-gray-500 -mt-2">
                When set, every Gate-IN/OUT tagged with this carrier will auto-email an e-Waybill PDF to this address. Leave blank to keep it manual (the "Waybill" button on the Gate Activity page still works either way).
              </div>
            </>
          )}
        </Section>

        {/* EDI (only meaningful if carrier) */}
        {form.roles.includes("carrier") && (
          <Section icon={FileText} title="EDI · CODECO">
            <div className="sm:col-span-3 text-xs text-gray-600 -mt-1 mb-2">
              When <span className="font-mono">Auto-send</span> is on, gate events for this carrier auto-emit a CODECO95B to the address below.
              In <span className="font-mono">BATCHED</span> mode events queue until you flush from the EDI Messages page.
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Partner profile</label>
              <select value={form.edi.partner_profile || ""} onChange={(e) => setNested("edi", "partner_profile", e.target.value || null)} className="ps-input font-mono" data-testid="edi-profile">
                <option value="">— none —</option>
                <option value="GENERIC">GENERIC</option>
                <option value="HAPAG_LLOYD">HAPAG_LLOYD</option>
                <option value="ARKAS">ARKAS</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Send mode</label>
              <select value={form.edi.batch_mode || "IMMEDIATE"} onChange={(e) => setNested("edi", "batch_mode", e.target.value)} className="ps-input font-mono" data-testid="edi-batch-mode">
                <option value="IMMEDIATE">IMMEDIATE</option>
                <option value="BATCHED">BATCHED</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.edi.auto_send !== false}
                  onChange={(e) => setNested("edi", "auto_send", e.target.checked)}
                  data-testid="edi-auto-send"
                  className="w-4 h-4"
                />
                Auto-send on gate events
              </label>
            </div>
            <Field label="Recipient email" value={form.edi.email || ""} onChange={(v) => setNested("edi", "email", v)} testid="field-edi-email" />
            <Field label="CC emails (comma-separated)" value={(form.edi.cc_emails || []).join(", ")} onChange={(v) => setNested("edi", "cc_emails", v.split(",").map((s) => s.trim()).filter(Boolean))} testid="field-edi-cc" />
            <Field label="Sender ID" value={form.edi.sender_id || ""} onChange={(v) => setNested("edi", "sender_id", v)} mono />
            <Field label="Receiver ID" value={form.edi.receiver_id || ""} onChange={(v) => setNested("edi", "receiver_id", v)} mono />
            <Field label="Depot code" value={form.edi.depot_code || ""} onChange={(v) => setNested("edi", "depot_code", v)} mono />
            <Field label="Depot location" value={form.edi.depot_location || ""} onChange={(v) => setNested("edi", "depot_location", v)} mono />
            <Field label="UNH prefix" value={form.edi.unh_prefix || ""} onChange={(v) => setNested("edi", "unh_prefix", v)} mono />
          </Section>
        )}

        {/* Daily Stock Report — available for any partner; especially useful for carriers & rental customers */}
        <Section icon={CalendarClock} title="Daily stock report">
          <div className="sm:col-span-3 text-xs text-gray-600 -mt-1 mb-2">
            When enabled, an automated stock snapshot is emailed to the recipients below every day at <span className="font-mono">08:00 Ghana time</span>.
            Choose the file format your customer prefers — PDF for a fixed, print-ready report, or Excel if they want to sort/filter or paste rows into their own system.
            Use <span className="font-mono">Preview</span> to see exactly what will be sent, or <span className="font-mono">Send now</span> to fire a one-off email immediately.
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={!!form.daily_stock_report?.enabled}
                onChange={(e) => setNested("daily_stock_report", "enabled", e.target.checked)}
                data-testid="dsr-enabled"
                className="w-4 h-4"
              />
              Enabled — send daily
            </label>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Template</label>
            <select
              value={form.daily_stock_report?.template || "CONTAINER"}
              onChange={(e) => setNested("daily_stock_report", "template", e.target.value)}
              className="ps-input font-mono"
              data-testid="dsr-template"
            >
              <option value="CONTAINER">CONTAINER (box carriers — HLC, ARK, …)</option>
              <option value="TANK">TANK (cleaning/repair columns visible)</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Report format</label>
            <select
              value={form.daily_stock_report?.format || "pdf"}
              onChange={(e) => setNested("daily_stock_report", "format", e.target.value)}
              className="ps-input font-mono"
              data-testid="dsr-format"
            >
              <option value="pdf">PDF — fixed, print-ready report</option>
              <option value="xlsx">Excel — sortable, filterable workbook</option>
            </select>
          </div>
          <div className="text-xs text-gray-500 self-end pb-1.5">
            {form.daily_stock_report?.last_sent_at ? (
              <>Last sent: <span className="font-mono">{form.daily_stock_report.last_sent_at.slice(0,16).replace('T',' ')}</span> ({form.daily_stock_report?.last_status || "—"})</>
            ) : (<>Never sent yet.</>)}
          </div>
          <Field
            label="Recipients (To, comma-separated)"
            value={(form.daily_stock_report?.recipients || []).join(", ")}
            onChange={(v) => setNested("daily_stock_report", "recipients", v.split(",").map((s) => s.trim()).filter(Boolean))}
            testid="dsr-recipients"
          />
          <Field
            label="CC (comma-separated)"
            value={(form.daily_stock_report?.cc_emails || []).join(", ")}
            onChange={(v) => setNested("daily_stock_report", "cc_emails", v.split(",").map((s) => s.trim()).filter(Boolean))}
            testid="dsr-cc"
          />
          <div className="sm:col-span-3 flex flex-wrap gap-2 pt-1">
            <a
              href={`${API_BASE}/stock-reports/preview/${encodeURIComponent(form.code || "")}?format=${form.daily_stock_report?.format || "pdf"}`}
              target="_blank"
              rel="noopener noreferrer"
              className={`ps-btn ps-btn-secondary !py-1.5 !px-3 text-xs ${!form.code ? "opacity-40 pointer-events-none" : ""}`}
              data-testid="dsr-preview"
            >
              <Eye className="w-3.5 h-3.5" /> Preview {(form.daily_stock_report?.format || "pdf").toUpperCase()}
            </a>
            <button
              type="button"
              className="ps-btn ps-btn-primary !py-1.5 !px-3 text-xs"
              disabled={!form.code || !(form.daily_stock_report?.recipients?.length)}
              onClick={async () => {
                try {
                  const res = await fetch(`${API_BASE}/stock-reports/send-now/${encodeURIComponent(form.code)}`, {
                    method: "POST", credentials: "include",
                  });
                  const json = await res.json();
                  if (!res.ok) throw new Error(json.detail || "Send failed");
                  toast.success(`Sent to ${(json.recipients || []).join(", ")}`);
                } catch (err) { toast.error(err.message); }
              }}
              data-testid="dsr-send-now"
            >
              <Send className="w-3.5 h-3.5" /> Send now
            </button>
          </div>
        </Section>

        {/* TAGS + LOGO + NOTES */}
        <Section icon={Tag} title="Tags">
          <div className="sm:col-span-3">
            <div className="flex flex-wrap gap-2 mb-2">
              {form.tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-xs rounded font-mono">
                  {t}
                  <button onClick={() => set("tags", form.tags.filter((x) => x !== t))} className="text-red-600 hover:text-red-800"><X className="w-3 h-3" /></button>
                </span>
              ))}
              {form.tags.length === 0 && <span className="text-xs text-gray-400">No tags</span>}
            </div>
            <div className="flex gap-2">
              <input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                placeholder="Add tag (press Enter)"
                className="ps-input"
                data-testid="field-tag-input"
              />
              <button className="ps-btn ps-btn-secondary" onClick={addTag}>Add</button>
            </div>
          </div>
        </Section>

        {/* ALIASES — alternate codes that resolve to this partner */}
        <Section icon={Tag} title="Aliases">
          <div className="sm:col-span-3">
            <div className="text-[11px] text-gray-500 mb-2">
              Alternate spellings or legacy codes that should resolve to <b className="font-mono">{form.code || "this partner"}</b>.
              Typed into gate events / wash orders → silently rewritten to the canonical code so invoicing & EDI stay consistent.
              <br/>Example: <span className="font-mono">SEVENLOG, SEVEN_LOG → 7LOG</span>
            </div>
            <div className="flex flex-wrap gap-2 mb-2">
              {form.aliases.map((a) => (
                <span key={a} className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 border border-amber-300 text-xs rounded font-mono" data-testid={`alias-chip-${a}`}>
                  {a}
                  <button
                    onClick={() => set("aliases", form.aliases.filter((x) => x !== a))}
                    className="text-red-600 hover:text-red-800"
                    data-testid={`alias-remove-${a}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {form.aliases.length === 0 && <span className="text-xs text-gray-400">No aliases</span>}
            </div>
            <div className="flex gap-2">
              <input
                value={aliasInput}
                onChange={(e) => setAliasInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const a = (aliasInput || "").toUpperCase().replace(/\s+/g, "").trim();
                    if (!a) return;
                    if (a === form.code) { toast.error("Alias cannot be the canonical code"); return; }
                    if (!form.aliases.includes(a)) set("aliases", [...form.aliases, a]);
                    setAliasInput("");
                  }
                }}
                placeholder="Add alias (press Enter) — e.g. SEVENLOG"
                className="ps-input font-mono"
                data-testid="field-alias-input"
              />
              <button
                className="ps-btn ps-btn-secondary"
                onClick={() => {
                  const a = (aliasInput || "").toUpperCase().replace(/\s+/g, "").trim();
                  if (!a) return;
                  if (a === form.code) { toast.error("Alias cannot be the canonical code"); return; }
                  if (!form.aliases.includes(a)) set("aliases", [...form.aliases, a]);
                  setAliasInput("");
                }}
                data-testid="field-alias-add"
              >Add</button>
            </div>
          </div>
        </Section>

        <Section icon={ImageIcon} title="Logo">
          <div className="sm:col-span-3 flex items-center gap-4">
            {form.logo_url ? (
              <img src={form.logo_url} alt="logo" className="w-20 h-20 object-contain border border-gray-200 bg-white p-2" />
            ) : (
              <div className="w-20 h-20 border border-dashed border-gray-300 flex items-center justify-center text-gray-300">
                <ImageIcon className="w-8 h-8" />
              </div>
            )}
            {!isNew ? (
              <label className="ps-btn ps-btn-secondary cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="logo-upload" />
                {uploadingLogo ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <>Choose file…</>}
              </label>
            ) : (
              <div className="text-xs text-gray-500 italic">Save the partner first, then come back to upload a logo.</div>
            )}
          </div>
        </Section>

        <Section icon={FileText} title="Notes">
          <div className="sm:col-span-3">
            <textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={4} className="ps-input" data-testid="field-notes" />
          </div>
        </Section>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="border border-gray-200 bg-white">
      <div className="px-5 py-3 border-b border-gray-200 flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#006E9E]" />
        <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-gray-700">{title}</span>
      </div>
      <div className="p-5 grid sm:grid-cols-3 gap-3">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", mono, cls, testid, placeholder }) {
  return (
    <div className={cls || ""}>
      <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`ps-input ${mono ? "font-mono" : ""}`}
        data-testid={testid}
      />
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────
// Per-depot tariff coverage chips. One chip per depot (🇬🇭 🇹🇬 🇬🇦) with a
// traffic-light fill:
//   • green = carrier tariff set (per-container rate card is in place)
//   • blue  = Water Solution flat fee set (but no carrier tariff — rare)
//   • dim   = nothing set for this depot
// For partners without the `carrier` role (customer-only, vendor-only)
// we hide the chips entirely — tariffs don't apply.
const CHIP_DEPOTS = [
  { code: "GH-TEM", flag: "\u{1F1EC}\u{1F1ED}" },
  { code: "TG-LFW", flag: "\u{1F1F9}\u{1F1EC}" },
  { code: "GA-LBV", flag: "\u{1F1EC}\u{1F1E6}" },
];

function TariffChips({ partner, coverage }) {
  const isCarrier = (partner.roles || []).includes("carrier");
  if (!isCarrier) {
    return <span className="text-[10px] text-gray-300 font-mono" data-testid={`tariff-na-${partner.code}`}>n/a</span>;
  }
  const partnerCov = coverage.coverage?.[partner.code] || {};
  return (
    <div className="flex flex-wrap gap-1" data-testid={`tariff-chips-${partner.code}`}>
      {CHIP_DEPOTS.map((d) => {
        const c = partnerCov[d.code] || {};
        const hasCarrier = !!c.carrier;
        const hasWs = !!c.ws;
        let cls, title;
        if (hasCarrier) {
          cls = "border-emerald-400 bg-emerald-50 text-emerald-800";
          title = `${d.code}: carrier tariff set${hasWs ? " + WS fee" : ""}`;
        } else if (hasWs) {
          cls = "border-sky-400 bg-sky-50 text-sky-800";
          title = `${d.code}: Water Solution fee only (no per-container tariff)`;
        } else {
          cls = "border-gray-200 bg-gray-50 text-gray-300";
          title = `${d.code}: no tariff set — carrier can't be invoiced here`;
        }
        return (
          <span
            key={d.code}
            title={title}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-mono ${cls}`}
            data-testid={`tariff-chip-${partner.code}-${d.code}`}
          >
            <span className="text-xs leading-none">{d.flag}</span>
            <span className="font-bold">{d.code.slice(0, 2)}</span>
          </span>
        );
      })}
    </div>
  );
}
